import React, { useState, useEffect } from 'react';
import { Input, Badge, Button } from 'reactstrap';
import { axiosApi } from '../../redux/actions';
import useDebounce from '../../hooks/useDebounce';

const CommentSearchSelect = ({
  value = '',
  onChange,
  section,
  placeholder = 'Buscar comentario...',
  disabled = false,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedComment, setSelectedComment] = useState(null);
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);

  // Debounced search function
  const { debounceFn: debouncedSearch } = useDebounce((searchTerm) => {
    setDebouncedSearchValue(searchTerm);
  }, 300);

  // Fetch comments from API
  const fetchComments = async (searchTerm) => {
    setIsLoading(true);
    try {
      const url =
        searchTerm.length === 0
          ? `api/v1/comment?section=${section}`
          : `api/v1/comment?section=${section}&value=${searchTerm}`;

      const response = await axiosApi.get(url);
      if (response.data && response.data.data) {
        setComments(response.data.data);
      } else {
        setComments([]);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
      setComments([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const searchTerm = e.target.value;
    setSearchValue(searchTerm);
    setShowSuggestions(true);
    debouncedSearch(searchTerm);
  };

  // Handle comment selection
  const handleCommentSelect = (comment) => {
    setSelectedComment(comment);
    setSearchValue('');
    setShowSuggestions(false);
    onChange(comment.value.toUpperCase());
  };

  // Handle removing selected comment
  const handleRemoveComment = () => {
    setSelectedComment(null);
    onChange('');
  };

  // Handle input blur with delay to allow clicking on suggestions
  const handleInputBlur = () => {
    setTimeout(() => setShowSuggestions(false), 200);
  };

  // Fetch comments when debounced search value changes
  useEffect(() => {
    fetchComments(debouncedSearchValue);
  }, [debouncedSearchValue, section]);

  // Load initial comments when component mounts or section changes
  useEffect(() => {
    if (section && !hasLoadedInitial) {
      fetchComments('');
      setHasLoadedInitial(true);
    }
  }, [section, hasLoadedInitial]);

  // Reset hasLoadedInitial when section changes
  useEffect(() => {
    setHasLoadedInitial(false);
    setComments([]);
    setShowSuggestions(false);
    setSelectedComment(null);
    setSearchValue('');
  }, [section]);

  // Initialize with existing value if provided
  useEffect(() => {
    if (value && !selectedComment) {
      // Create a mock comment object for existing values
      setSelectedComment({ value: value });
    }
  }, [value, selectedComment]);

  return (
    <div className="position-relative">
      {selectedComment ? (
        <div className="d-flex align-items-center">
          <Badge color="primary" className="mr-2 p-2">
            {selectedComment.value}
          </Badge>
          <Button
            color="link"
            size="sm"
            className="p-0"
            onClick={handleRemoveComment}
            disabled={disabled}
          >
            <i className="fa fa-times"></i>
          </Button>
        </div>
      ) : (
        <Input
          type="text"
          placeholder={placeholder}
          value={searchValue}
          onChange={handleSearchChange}
          onBlur={handleInputBlur}
          onFocus={() => {
            setShowSuggestions(true);
            // If no search value and we have initial comments loaded, show them
            if (
              searchValue.length === 0 &&
              hasLoadedInitial &&
              comments.length > 0
            ) {
              // Comments are already loaded, just show suggestions
            }
          }}
          disabled={disabled}
        />
      )}

      {showSuggestions && !selectedComment && (
        <div
          className="position-absolute w-100 bg-white border rounded-bottom"
          style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}
        >
          {isLoading ? (
            <div className="px-3 py-2">
              <p className="mb-0 text-muted">Cargando comentarios...</p>
            </div>
          ) : comments.length > 0 ? (
            comments.map((comment) => (
              <div
                key={comment.id}
                className="px-3 py-2 cursor-pointer hover-bg-light"
                onClick={() => handleCommentSelect(comment)}
                style={{ cursor: 'pointer' }}
                onMouseEnter={(e) =>
                  (e.target.style.backgroundColor = '#f8f9fa')
                }
                onMouseLeave={(e) =>
                  (e.target.style.backgroundColor = 'transparent')
                }
              >
                <span className="font-weight-bold">{comment.value}</span>
                {/* {comment.section && (
                  <>
                    <br />
                    <span className="text-muted small">
                      Sección: {comment.section}
                    </span>
                  </>
                )} */}
              </div>
            ))
          ) : (
            <div className="px-3 py-2">
              <p className="mb-0 text-muted">
                {searchValue
                  ? 'No se encontraron comentarios'
                  : 'No hay comentarios disponibles'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CommentSearchSelect;
